<template>
  <LinkWrapper
    :href="href"
    :class="[classes.offerButton]"
    target="_blank"
    :title="text"
  >
    <StyledText
      tag="span"
      :fontId="buttonStyle.primaryFont"
      :class="[classes.ctaText, 'ctaText']"
    >
      {{ text }}
    </StyledText>
  </LinkWrapper>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/ad-data-composition/src/theme";
import autoInteractions from "../../lib/autoInteractions";
import LinkWrapper from "../LinkWrapper.vue";
import {
  ButtonStyle,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { defineComponent, computed } from "vue";
import StyledText from "./Text.vue";

export default defineComponent({
  name: "OfferButton",
  components: {
    LinkWrapper,
    StyledText
  },
  props: {
    href: {
      type: String,
      required: false,
      default: ""
    },
    text: {
      type: String,
      required: false
    },
    buttonId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const brand = injectBrand();
    const buttonStyle = computed(() =>
      brand?.getButtonStyle(props.buttonId, true)
    );
    const styleComputed = computed(() => ({
      buttonStyle: buttonStyle.value,
      primaryColor:
        buttonStyle.value &&
        brand?.getFontStyle(buttonStyle.value.primaryFont)?.color
    }));

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const { classes } = useStyles(styleComputed);

    return {
      classes,
      buttonStyle,
      testClassName: computed(() => "text-style-market-name")
    };
  }
});

const defaultStyling = {
  backgroundColor: "#fff",
  borderRadius: "",
  borderColor: "",
  borderStyle: "",
  borderWidth: "",
  autoInteractions: true
};

const { useStyles } = createUseStyles<
  "offerButton" | "ctaText",
  {
    buttonStyle: ButtonStyle;
    primaryColor: string;
  }
>({
  offerButton: props => {
    const buttonStyled = props?.buttonStyle || defaultStyling;
    const backgroundColor =
      buttonStyled.backgroundColor || defaultStyling.backgroundColor;
    const borderRadius =
      buttonStyled.borderRadius || defaultStyling.borderRadius;
    const borderColor = buttonStyled.borderColor || defaultStyling.borderColor;
    const borderStyle = buttonStyled.borderStyle || defaultStyling.borderStyle;
    const borderWidth = buttonStyled.borderWidth || defaultStyling.borderWidth;

    const autoInteractionsStyles = autoInteractions({
      backgroundColor,
      primaryColor: props?.primaryColor || "#000"
    });

    const actionStates = buttonStyled.autoInteractions
      ? autoInteractionsStyles
      : props.buttonStyle?.actions;

    const actionClasses = {
      "& $ctaText": {
        color: props.primaryColor || "#000"
      },
      "&:hover": {
        backgroundColor:
          actionStates?.hover?.backgroundColor ||
          autoInteractionsStyles.hover.backgroundColor,
        "& $ctaText": {
          color:
            actionStates?.hover?.primaryColor ||
            autoInteractionsStyles.hover.primaryColor
        }
      },
      "&:active": {
        backgroundColor:
          actionStates?.active?.backgroundColor ||
          autoInteractionsStyles.hover.backgroundColor,
        "& $ctaText": {
          color:
            actionStates?.active?.primaryColor ||
            autoInteractionsStyles.active.primaryColor
        }
      }
    };

    return {
      backgroundColor,
      borderRadius,
      borderColor,
      borderStyle,
      borderWidth,
      ...actionClasses,
      "& > div": {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
        alignItems: "center",
        position: "relative"
      },
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      boxSizing: "border-box",
      padding: "4px 8px"
    };
  },
  ctaText: {
    fontSize: "14px",
    lineHeight: 1.1,
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
    wordBreak: "break-word"
  }
});
</script>
